import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OwnerProposal — public owner-facing picker.
 *
 * Route: /proposal/:token
 *
 * Model (locked with Shannon):
 *   MF pre-composes CURATED Design Solutions per apartment complex.
 *   Owner picks ONE Solution — they don't build one from raw components.
 *   Every Solution is Section 8 · HUD-VASH · Housing Coalition compliant.
 */
export default function OwnerProposal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [solutions, setSolutions] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [chosenId, setChosenId] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerMessage, setOwnerMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/proposals/${token}`);
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          setError(d.detail || "This link is no longer valid.");
          setLoading(false);
          return;
        }
        const d = await r.json();
        setProposal(d.proposal);
        setSolutions(d.catalog?.solutions || []);
        setDisclaimer(d.catalog?.disclaimer || "");
        setOwnerName(d.proposal.owner_name || "");
        if (d.proposal.chosen_solution_id) setChosenId(d.proposal.chosen_solution_id);
        if (d.proposal.state === "submitted" || d.proposal.state === "converted") {
          setSubmitted(d.priced || null);
        }
      } catch {
        setError("Network error. Try refreshing.");
      }
      setLoading(false);
    })();
  }, [token]);

  const submit = async () => {
    if (!chosenId) { setError("Pick one Design Solution first."); return; }
    setSubmitting(true); setError("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/proposals/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          solution_id: chosenId,
          owner_name: ownerName,
          owner_message: ownerMessage,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "Could not submit."); setSubmitting(false); return; }
      setSubmitted(d.priced);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("Network error. Try again.");
    }
    setSubmitting(false);
  };

  if (loading) return <FullPageMsg>Loading your proposal…</FullPageMsg>;
  if (error && !proposal) return <FullPageMsg tone="error">{error}</FullPageMsg>;

  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #06121F 0%, #0B1A2E 100%)", padding: "48px 20px", color: "#F3F4F6" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ background: "rgba(0,220,120,0.08)", border: "1px solid rgba(0,220,120,0.35)", borderRadius: 14, padding: "1.6rem 1.7rem" }}>
            <div style={{ color: "#00DC78", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", marginBottom: 6 }}>✓ SUBMITTED · MOOVIN FORWARD HAS BEEN NOTIFIED</div>
            <h1 style={{ fontSize: "1.8rem", fontWeight: 800, margin: "6px 0 8px" }}>Thanks — we&apos;ve got it from here.</h1>
            <p style={{ color: "#ADBED1", fontSize: "0.95rem", lineHeight: 1.55, margin: 0 }}>
              Your Design Solution is locked in. Our team will send back the formal flat-fee proposal (with seasonal adjustment) within one business day.
            </p>
          </div>

          <div style={{ marginTop: 20, background: "rgba(15,20,30,0.6)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "1.4rem" }}>
            <p style={{ color: "#5AC8D8", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", margin: "0 0 4px" }}>YOUR CHOICE</p>
            <h2 style={{ fontSize: "1.35rem", fontWeight: 800, margin: "0 0 4px" }}>{submitted.solution.name}</h2>
            <p style={{ color: "#5AC8D8", fontSize: "0.85rem", margin: "0 0 12px", fontStyle: "italic" }}>{submitted.solution.tagline}</p>
            <PalettePreview palette={submitted.palette} compact />
            <ComplianceBadges compliance={submitted.compliance} />
            <p style={{ color: "#ADBED1", fontSize: "0.85rem", margin: "12px 0 0", lineHeight: 1.5 }}>{submitted.solution.narrative}</p>
          </div>

          <p style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: 24, textAlign: "center", lineHeight: 1.5 }}>{disclaimer}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="owner-proposal-page" style={{ minHeight: "100vh", background: "linear-gradient(180deg, #06121F 0%, #0B1A2E 100%)", padding: "36px 18px 120px", color: "#F3F4F6" }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ color: "#5AC8D8", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", margin: 0 }}>MOOVIN FORWARD · OWNER PROPOSAL</p>
          <h1 style={{ fontSize: "clamp(1.7rem, 5vw, 2.3rem)", fontWeight: 800, margin: "6px 0 10px", lineHeight: 1.15 }}>
            Pick your Design Solution.
          </h1>
          <p style={{ color: "#ADBED1", fontSize: "0.95rem", lineHeight: 1.55, margin: 0 }}>
            Our team has curated the Design Solutions below for your apartment complex — each one is <strong style={{ color: "#F3F4F6" }}>Section 8, HUD-VASH, and Housing Coalition compliant</strong>. Every solution uses interchangeable, removable finishes (no permits, no plumbing, no electrical).
          </p>
          {proposal?.project_address && (
            <p style={{ color: "#7E92A8", fontSize: "0.82rem", marginTop: 10 }}>📍 {proposal.project_address}</p>
          )}
        </div>

        {/* Solution picker */}
        {solutions.length === 0 ? (
          <div style={{ padding: 24, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, color: "#FCA5A5", fontSize: "0.9rem" }}>
            No Design Solutions available on this proposal yet. Please contact Moovin Forward.
          </div>
        ) : (
          <div data-testid="solutions-grid" style={{ display: "grid", gap: 16 }}>
            {solutions.map(s => {
              const selected = chosenId === s.id;
              const expanded = expandedId === s.id;
              return (
                <div
                  key={s.id}
                  data-testid={`solution-${s.id}`}
                  onClick={() => setChosenId(s.id)}
                  style={{
                    background: selected ? "rgba(90,200,216,0.08)" : "rgba(15,20,30,0.6)",
                    border: `2px solid ${selected ? "#5AC8D8" : "rgba(255,255,255,0.08)"}`,
                    borderRadius: 14,
                    padding: "1.15rem 1.25rem",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start", justifyContent: "space-between" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                        <h2 style={{ fontSize: "1.15rem", fontWeight: 800, margin: 0 }}>{s.name}</h2>
                        {selected && (
                          <span style={{ background: "#5AC8D8", color: "#062617", padding: "2px 8px", borderRadius: 999, fontSize: "0.7rem", fontWeight: 800 }}>SELECTED</span>
                        )}
                      </div>
                      <p style={{ color: "#5AC8D8", fontSize: "0.85rem", fontWeight: 600, margin: "0 0 10px", fontStyle: "italic" }}>{s.tagline}</p>
                      <SolutionCoverImage solution={s} />
                      <PalettePreview palette={s.palette} compact />
                      <ComplianceBadges compliance={s.compliance} />
                    </div>
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%",
                      border: `2px solid ${selected ? "#5AC8D8" : "rgba(255,255,255,0.2)"}`,
                      background: selected ? "#5AC8D8" : "transparent",
                      color: selected ? "#062617" : "transparent",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 800, fontSize: 16, flexShrink: 0,
                    }}>{selected ? "✓" : ""}</div>
                  </div>

                  <p style={{ color: "#ADBED1", fontSize: "0.87rem", lineHeight: 1.55, margin: "12px 0 8px" }}>{s.narrative}</p>

                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setExpandedId(expanded ? "" : s.id); }}
                    data-testid={`solution-${s.id}-details-toggle`}
                    style={{ background: "transparent", border: "none", color: "#5AC8D8", padding: 0, fontSize: "0.82rem", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
                  >
                    {expanded ? "Hide details ↑" : "See what's included ↓"}
                  </button>

                  {expanded && (
                    <div style={{ marginTop: 12, padding: "12px 14px", background: "rgba(6,12,20,0.5)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10 }}>
                      <p style={{ color: "#7E92A8", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", margin: "0 0 8px" }}>DESIGN GALLERY</p>
                      <SolutionGalleryGrid solution={s} />
                      <p style={{ color: "#7E92A8", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.08em", margin: "16px 0 8px" }}>INCLUDED MODULES</p>
                      {s.modules.map(m => (
                        <div key={m.id} style={{ padding: "6px 0", borderBottom: "1px dashed rgba(255,255,255,0.06)" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                            <span style={{ color: "#E6ECF2", fontSize: "0.87rem", fontWeight: 600 }}>{m.name}</span>
                            <span style={{ color: "#7E92A8", fontSize: "0.75rem" }}>{m.category}</span>
                          </div>
                          <p style={{ color: "#ADBED1", fontSize: "0.78rem", margin: "3px 0 0", lineHeight: 1.45 }}>{m.description}</p>
                        </div>
                      ))}
                      <p style={{ color: "#7E92A8", fontSize: "0.75rem", margin: "10px 0 0", fontStyle: "italic", lineHeight: 1.5 }}>
                        <strong>Compliance:</strong> {s.compliance_notes}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Owner info */}
        <div style={{ marginTop: 32 }}>
          <SectionTitle num="2" label="Confirm your info" />
          <input
            data-testid="owner-name"
            type="text"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Your name"
            style={inputStyle}
          />
          <textarea
            data-testid="owner-message"
            value={ownerMessage}
            onChange={(e) => setOwnerMessage(e.target.value)}
            placeholder="Anything specific you want us to know? (optional)"
            rows={3}
            style={{ ...inputStyle, marginTop: 10, resize: "vertical", minHeight: 80 }}
          />
        </div>

        {/* Sticky CTA */}
        <div style={{ position: "sticky", bottom: 0, marginTop: 32, padding: "1rem 0.9rem 1.2rem", background: "linear-gradient(180deg, rgba(6,18,31,0) 0%, rgba(6,18,31,0.95) 40%, rgba(6,18,31,1) 100%)" }}>
          <div style={{ background: "rgba(15,20,30,0.9)", border: "1px solid rgba(90,200,216,0.35)", borderRadius: 14, padding: "1rem 1.2rem", backdropFilter: "blur(10px)" }}>
            {error && <div style={{ color: "#FCA5A5", fontSize: "0.82rem", marginBottom: 8 }}>{error}</div>}
            <button
              data-testid="submit-proposal"
              onClick={submit}
              disabled={submitting || !chosenId}
              style={{
                width: "100%",
                background: chosenId ? "linear-gradient(90deg, #00DC78, #00B364)" : "rgba(255,255,255,0.08)",
                color: chosenId ? "#062617" : "#7E92A8",
                border: "none", borderRadius: 10, padding: "1rem",
                fontSize: "1rem", fontWeight: 800,
                cursor: (submitting || !chosenId) ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Submitting…" : chosenId ? "→ Lock in my Design Solution" : "Pick one Design Solution above"}
            </button>
            <p style={{ color: "#7E92A8", fontSize: "0.7rem", textAlign: "center", margin: "10px 0 0" }}>
              Your final flat fee — labor, materials, seasonal adjustment — will be on the formal proposal we send back.
            </p>
          </div>
        </div>

        <p style={{ color: "#546274", fontSize: "0.7rem", marginTop: 32, textAlign: "center", lineHeight: 1.5 }}>{disclaimer}</p>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  background: "rgba(15,20,30,0.6)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 10,
  padding: "0.85rem 1rem",
  color: "#F3F4F6",
  fontSize: "0.95rem",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const SectionTitle = ({ num, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
    <span style={{ background: "rgba(90,200,216,0.15)", color: "#5AC8D8", width: 28, height: 28, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>{num}</span>
    <h2 style={{ fontSize: "1.1rem", fontWeight: 700, margin: 0 }}>{label}</h2>
  </div>
);

const PalettePreview = ({ palette, compact = false }) => (
  palette ? (
    <>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        {palette.colors.map(c => (
          <div
            key={c.role}
            title={`${c.label} · ${c.role}`}
            style={{
              flex: 1,
              aspectRatio: "3/1",
              background: c.hex,
              borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.08)",
              maxHeight: compact ? 28 : 40,
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: "0.78rem", color: "#7E92A8" }}>
        <strong style={{ color: "#E6ECF2" }}>{palette.name}</strong> · {palette.tagline}
      </div>
    </>
  ) : null
);

const ComplianceBadges = ({ compliance = {} }) => {
  const badges = [
    { key: "section_8",         label: "Section 8" },
    { key: "hud_vash",          label: "HUD-VASH" },
    { key: "housing_coalition", label: "Housing Coalition" },
    { key: "nspire_ready",      label: "NSPIRE Ready" },
  ].filter(b => compliance[b.key]);
  if (badges.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
      {badges.map(b => (
        <span
          key={b.key}
          data-testid={`compliance-${b.key}`}
          style={{
            background: "rgba(0,220,120,0.10)",
            color: "#4ADE80",
            border: "1px solid rgba(0,220,120,0.3)",
            padding: "2px 8px",
            borderRadius: 999,
            fontSize: "0.66rem",
            fontWeight: 800,
            letterSpacing: "0.03em",
          }}
        >✓ {b.label}</span>
      ))}
    </div>
  );
};

const FullPageMsg = ({ children, tone = "info" }) => (
  <div style={{ minHeight: "100vh", background: "#06121F", color: tone === "error" ? "#FCA5A5" : "#ADBED1", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", fontSize: "1rem" }}>
    {children}
  </div>
);

// Cover image on each Solution card. Uses the gallery_urls.cover endpoint —
// falls back to a palette-swatch banner when the AI hasn't produced a photo yet.
function SolutionCoverImage({ solution }) {
  const [failed, setFailed] = useState(false);
  const url = solution?.gallery_urls?.cover;
  if (!url || failed) {
    return (
      <div style={{
        borderRadius: 10, marginBottom: 10, height: 140,
        background: `linear-gradient(135deg, ${(solution?.palette?.colors?.[0]?.hex)||"#1A1A1A"}, ${(solution?.palette?.colors?.[2]?.hex)||"#333"})`,
        border: "1px solid rgba(255,255,255,0.08)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", letterSpacing: "0.1em", fontWeight: 700,
      }}>PALETTE PREVIEW</div>
    );
  }
  return (
    <img
      data-testid={`solution-${solution.id}-cover`}
      src={`${BACKEND_URL}${url}`}
      alt={solution.name}
      onError={() => setFailed(true)}
      style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 10, marginBottom: 10, border: "1px solid rgba(255,255,255,0.08)" }}
    />
  );
}

// 4-slot expandable gallery grid (cover, kitchen, bedroom, bathroom).
function SolutionGalleryGrid({ solution }) {
  const slots = [
    { key: "cover",    label: "Living Area" },
    { key: "kitchen",  label: "Kitchen" },
    { key: "bedroom",  label: "Bedroom" },
    { key: "bathroom", label: "Bathroom" },
  ];
  return (
    <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
      {slots.map(sl => (
        <GallerySlot key={sl.key} url={solution?.gallery_urls?.[sl.key]} label={sl.label} palette={solution?.palette} solutionId={solution.id} slot={sl.key} />
      ))}
    </div>
  );
}

function GallerySlot({ url, label, palette, solutionId, slot }) {
  const [failed, setFailed] = useState(false);
  const swatch = palette?.colors?.[0]?.hex || "#1A1A1A";
  const swatch2 = palette?.colors?.[2]?.hex || "#333";
  return (
    <div data-testid={`solution-${solutionId}-slot-${slot}`}>
      {url && !failed ? (
        <img
          src={`${BACKEND_URL}${url}`}
          alt={label}
          onError={() => setFailed(true)}
          style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)" }}
        />
      ) : (
        <div style={{
          height: 110, borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)",
          background: `linear-gradient(135deg, ${swatch}, ${swatch2})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "rgba(255,255,255,0.45)", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.08em", textAlign: "center", padding: 6,
        }}>PREVIEW COMING SOON</div>
      )}
      <p style={{ color: "#7E92A8", fontSize: "0.7rem", margin: "4px 0 0", textAlign: "center", fontWeight: 700, letterSpacing: "0.04em" }}>{label}</p>
    </div>
  );
}
